import React from "react";
import { useTranslation } from "react-i18next";
import { Alert } from "@mui/material";
import { useLoadingIndicator } from "../../common/context/LoadingIndicatorContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleExclamation } from "@fortawesome/pro-regular-svg-icons";
import { ApiContext } from "../../common/context/ApiContext";
import {
  AnalyticsContext,
  WebContentLoadFailedEvent,
  WebContentLoadSuccessfulEvent,
} from "../../common/context/AnalyticsContext";

enum Status {
  OK = 0,
  ERROR = 1,
}

enum ContentLoadingStatus {
  INIT = 0,
  STARTED_LOADING = 1,
  FINISHED_LOADING = 2,
  ERROR = 3,
}

export const WebContentLessonPlayer = ({
  lessonId,
  lessonVersion,
  lessonTitle,
  webContentUrl,
  profileId,
}: {
  lessonId: number;
  lessonVersion: number;
  lessonTitle: string;
  webContentUrl: string;
  profileId: number;
}) => {
  const { t } = useTranslation();
  const { startLoading, stopLoading } = useLoadingIndicator(true);
  const loadingTimeoutId = React.useRef<number>();
  const updateIntervalId = React.useRef<number>();
  const [status, setStatus] = React.useState(Status.OK);
  const contentLoadingStatus = React.useRef(ContentLoadingStatus.INIT);
  const { createLessonExecution, maybeUpdateLessonExecution } =
    React.useContext(ApiContext);
  const lessonExecutionId = React.useRef<string>();
  const iframeActivatedAt = React.useRef<Date | undefined>();
  const activeTimeSinceLastUpdate = React.useRef(0);
  const { logEvent } = React.useContext(AnalyticsContext);

  const startLoadingTimer = React.useCallback(
    (ms) => {
      if (loadingTimeoutId.current) {
        window.clearTimeout(loadingTimeoutId.current);
      }
      loadingTimeoutId.current = window.setTimeout(() => {
        stopLoading();
        setStatus(Status.ERROR);
      }, ms);
    },
    [setStatus, stopLoading]
  );
  const stopLoadingTimer = React.useCallback(() => {
    if (loadingTimeoutId.current) {
      window.clearTimeout(loadingTimeoutId.current);
      loadingTimeoutId.current = undefined;
    }
  }, []);

  const updateCurrentLessonExecution = React.useCallback(() => {
    // If the iframe is "active", then record how long it's been active so far and reset the timer
    if (iframeActivatedAt.current) {
      const now = new Date();
      activeTimeSinceLastUpdate.current +=
        (now.getTime() - iframeActivatedAt.current.getTime()) / 1000.0;
      iframeActivatedAt.current = now;
    }
    if (activeTimeSinceLastUpdate.current > 0 && lessonExecutionId.current) {
      maybeUpdateLessonExecution(
        lessonId,
        lessonExecutionId.current,
        activeTimeSinceLastUpdate.current,
        false
      ).then((isSuccess) => {
        if (isSuccess) {
          activeTimeSinceLastUpdate.current = 0;
        }
      });
    }
  }, [
    lessonId,
    maybeUpdateLessonExecution,
    lessonExecutionId,
    iframeActivatedAt,
    activeTimeSinceLastUpdate,
  ]);

  const startTrackingLessonExecution = React.useCallback(() => {
    createLessonExecution(lessonId, lessonVersion, profileId).then(
      ({ lesson_execution_id }) => {
        lessonExecutionId.current = lesson_execution_id;
        updateIntervalId.current = window.setInterval(
          updateCurrentLessonExecution,
          30 * 1000
        );
      }
    );
  }, [
    lessonId,
    lessonVersion,
    profileId,
    createLessonExecution,
    lessonExecutionId,
    updateIntervalId,
    updateCurrentLessonExecution,
  ]);

  const stopTrackingLessonExecution = React.useCallback(() => {
    if (updateIntervalId.current) {
      window.clearInterval(updateIntervalId.current);
      updateIntervalId.current = undefined;
    }
  }, [updateIntervalId]);

  React.useEffect(() => {
    startLoading();
    // 30 seconds for web content to tell us that it started loading
    startLoadingTimer(30 * 1000);
    const onMessage = (event) => {
      if (typeof event.data === "string") {
        try {
          const data = JSON.parse(event.data);
          const type = data["type"];

          if (
            type === "didStartLoadingWebContent" &&
            contentLoadingStatus.current === ContentLoadingStatus.INIT
          ) {
            contentLoadingStatus.current = ContentLoadingStatus.STARTED_LOADING;
            startTrackingLessonExecution();
            // 30 seconds for web content to finish loading
            startLoadingTimer(30 * 1000);
          } else if (
            type === "didFinishLoadingWebContent" &&
            contentLoadingStatus.current ===
              ContentLoadingStatus.STARTED_LOADING
          ) {
            logEvent(
              new WebContentLoadSuccessfulEvent(
                lessonId,
                lessonTitle,
                webContentUrl
              )
            );
            contentLoadingStatus.current =
              ContentLoadingStatus.FINISHED_LOADING;
            stopLoadingTimer();
            stopLoading();
          } else if (type === "didFailLoadingWebContent") {
            logEvent(
              new WebContentLoadFailedEvent(
                lessonId,
                lessonTitle,
                webContentUrl
              )
            );
            contentLoadingStatus.current = ContentLoadingStatus.ERROR;
            stopLoadingTimer();
            stopTrackingLessonExecution();
            stopLoading();
            setStatus(Status.ERROR);
          } else if (type === "didBecomeActive") {
            // Keep track when this happened so that we can calculate delta duration
            if (!iframeActivatedAt.current) {
              iframeActivatedAt.current = new Date();
            }
          } else if (type === "didBecomeIdle") {
            if (iframeActivatedAt.current) {
              // Track cumulative active duration between when we became active and now
              const now = new Date();
              activeTimeSinceLastUpdate.current +=
                (now.getTime() - iframeActivatedAt.current.getTime()) / 1000.0;
              iframeActivatedAt.current = undefined;
            }
          }
        } catch {
          // ignore unrecognized messages
        }
      }
    };

    window.addEventListener("message", onMessage);

    // Clean up timers and event listeners when component is unmounted
    return () => {
      stopLoadingTimer();
      stopTrackingLessonExecution();
      window.removeEventListener("message", onMessage);
    };
  }, [
    startLoading,
    stopLoading,
    startLoadingTimer,
    stopLoadingTimer,
    setStatus,
    startTrackingLessonExecution,
    stopTrackingLessonExecution,
    logEvent,
    lessonId,
    lessonTitle,
    webContentUrl,
  ]);

  if (status === Status.OK) {
    return (
      <iframe
        title="Web content lesson"
        className="w-100"
        style={{ minHeight: "75vh" }}
        src={webContentUrl}
      ></iframe>
    );
  } else {
    return (
      <Alert
        icon={<FontAwesomeIcon icon={faCircleExclamation} fontSize="inherit" />}
        severity="error"
        style={{ marginBottom: "15px" }}
      >
        {t("web_content_not_available")}
      </Alert>
    );
  }
};
